import { PaginatedResponse, ProductType, Scheme, SchemeType } from "../types";
import { api } from "./axios";

interface SchemeListResponse<T extends SchemeType> {
  count: number;
  next: string | null;
  previous: string | null;
  results: Scheme<T>[];
}

export type SchemeApiOptions = {
  page?: string;
  page_size?: string;
  type: SchemeType;
  frequency?: string;
  ids?: number[];
  search?: string;
  isin?: string;
  product_type?: ProductType;
  amc_id?: number;
};

const PAGE_SIZE = 50;

export const getSchemeList = <T extends SchemeType>(
  options: SchemeApiOptions
): Promise<SchemeListResponse<T>> => {
  const {
    page,
    type = "",
    ids,
    frequency,
    page_size,
    search,
    isin,
    amc_id,
    product_type,
  } = options;

  const params = {
    ids,
    page,
    page_size: page_size || PAGE_SIZE,
    frequency,
    search,
    isin,
    amc_id,
    product_type,
  };

  switch (type) {
    case "sip":
      return api.get(`metadata/sip-schemes/`, {
        params,
      });
    case "stp":
      return api.get(`metadata/stp-schemes/`, {
        params,
      });
    case "swp":
      return api.get(`metadata/swp-schemes/`, {
        params,
      });
    default:
      return api.get(`onetime-schemes/`, {
        params,
      });
  }
};

export const getScheme = <T extends SchemeType>(
  options: Pick<SchemeApiOptions, "type"> & { id: number }
): Promise<Scheme<T>> => {
  const { type = "", id } = options;
  switch (type) {
    case "sip":
      return api.get(`metadata/sip-schemes/${id}/`);
    case "stp":
      return api.get(`metadata/stp-schemes/${id}/`);
    case "swp":
      return api.get(`metadata/swp-schemes/${id}/`);
    default:
      return api.get(`onetime-schemes/${id}/`);
  }
};

interface SchemeMaster {
  fund_scheme_id: number;
  scheme_code: string;
  scheme_type: string;
  isin: string;
  name: string;
  new_purchase_min_amount: number;
  additional_purchase_min_amount: number;
  additional_purchase_max_amount: string;
  sip_allowed: boolean;
  swp_allowed: boolean;
  stp_allowed: boolean;
  rta_scheme_code: string;
  active: boolean;
  div_reinvest_flag: string;
  scheme_start_date: string;
  maturity_date: string;
  exit_load_flag: boolean;
  exit_load: string;
  lock_in_period_flag: boolean;
  lock_in_period: number;
  reopening_date: string;
  purchase_amount_multiplier: string;
  redemption_amount_multiplier: string;
  redemption_units_multiplier: string;
  purchase_allowed: boolean;
  redemption_allowed: boolean;
  switch_allowed: boolean;
  created_at: string;
  updated_at: string;
  amc: number;
}

interface SchemeMasterParams {
  page?: number;
  pageSize?: number;
  isin?: string;
}

export function getSchemeMasterList(
  params: SchemeMasterParams
): Promise<PaginatedResponse<SchemeMaster>> {
  return api.get("schemes/", {
    params: {
      page: params.page,
      page_size: params.pageSize,
      isin: params.isin,
    },
  });
}

export function getSchemeMaster(id: number): Promise<SchemeMaster> {
  return api.get(`schemes/${id}/`);
}
